import { useState } from "react";

import { useNavigation, useNotification } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { WeeklyCalendar } from "@/pages/tina/groups/components/weekly-calendar";
import { useGroupsRepository } from "@/pages/tina/groups/hooks/use-groups-repository";

export const GroupsList = () => {
  const repo = useGroupsRepository();
  const { create, edit } = useNavigation();
  const { open } = useNotification();
  const [busyId, setBusyId] = useState<string | null>(null);

  const query = useQuery({ queryKey: ["tina-groups", "list"], queryFn: () => repo.list() });

  async function onRunNow(id: string) {
    if (busyId === id) return;
    setBusyId(id);
    try {
      await repo.runNow(id);
    } catch {
      // repo already surfaced the error
    } finally {
      setBusyId(null);
    }
  }

  async function onDelete(id: string) {
    if (busyId === id) return;
    setBusyId(id);
    try {
      await repo.remove(id);
      await query.refetch();
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to delete group",
        description: toHumanErrorMessage(error),
      });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="flex w-full max-w-none flex-col gap-4 p-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Groups</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Scheduled site groups by weekday. Times are in Europe/Athens.
          </p>
        </div>
        <Button onClick={() => create("tina-groups")}>
          <Plus className="mr-2 h-4 w-4" />
          Create group
        </Button>
      </div>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <WeeklyCalendar
          groups={query.data ?? []}
          onEdit={(id) => edit("tina-groups", id)}
          onRunNow={onRunNow}
          onDelete={onDelete}
          busyId={busyId}
        />
      )}
    </div>
  );
};
